import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const PlayerContext = createContext();

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [currentSong, setCurrentSong] = useState('Carregando...');
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);

  const STREAM_URL = '/stream';

  useEffect(() => {
    // Set initial volume
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const res = await fetch('/api/radio/now-playing');
        if (res.ok) {
          const data = await res.json();
          if (data?.currentTrack) {
            const rawArtist = (data.currentTrack.artist || '').trim();
            const rawTitle = (data.currentTrack.title || '').trim();
            const cleanArtist = rawArtist.replace(/^(DJ\s+)+/i, 'DJ ').trim();
            const cleanTitle = rawTitle.replace(/^(DJ\s+)+/i, 'DJ ').trim();

            if (cleanArtist && cleanTitle) {
              if (cleanTitle.toLowerCase().startsWith(cleanArtist.toLowerCase())) {
                setCurrentSong(cleanTitle);
              } else {
                setCurrentSong(`${cleanArtist} - ${cleanTitle}`);
              }
            } else {
              setCurrentSong(cleanTitle || cleanArtist || 'NostalBeat Web Rádio');
            }
          } else if (data?.currentProgram) {
            setCurrentSong(`NostalBeat - ${data.currentProgram}`);
          }
        }
      } catch (e) {
        // Fallback
      }
    };

    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 10000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) {
      console.error('Audio element not found');
      return;
    }

    const audio = audioRef.current;
    const isCurrentlyActive = isPlaying || isLoading || (!audio.paused && audio.currentTime > 0);

    if (isCurrentlyActive) {
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.src = '';
        audio.load();
      } catch (e) {}
      setIsPlaying(false);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      audio.volume = volume / 100;
      audio.src = `${STREAM_URL}?t=${Date.now()}`;
      audio.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch(error => {
          if (error.name === 'AbortError') return;
          console.error('Error playing audio:', error);
          setIsPlaying(false);
          setIsLoading(false);
        });
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  const handleLoadedData = () => {
    setIsLoading(false);
  };

  const handleError = (error) => {
    console.error('Audio error:', error);
    console.error('Audio error details:', {
      error: error,
      src: audioRef.current?.src,
      networkState: audioRef.current?.networkState,
      readyState: audioRef.current?.readyState
    });
    setIsLoading(false);
    setIsPlaying(false);
  };

  const value = {
    isPlaying,
    volume,
    currentSong,
    isLoading,
    togglePlay,
    handleVolumeChange,
    audioRef,
    STREAM_URL,
    handleLoadedData,
    handleError
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        src={STREAM_URL}
        onLoadedData={handleLoadedData}
        onError={handleError}
        onLoadStart={() => console.log('Audio load started')}
        onCanPlay={() => console.log('Audio can play')}
        onPlay={() => console.log('Audio play event')}
        onPause={() => console.log('Audio pause event')}
        preload="none"
        crossOrigin="anonymous"
        controls={false}
        style={{ display: 'none' }}
      />
      {/* Centova Stream Info Element (hidden) */}
      <span 
        className="cc_streaminfo" 
        data-type="song" 
        data-username="fabiogarcia"
        style={{ display: 'none' }}
      >
        Carregando...
      </span>
    </PlayerContext.Provider>
  );
};
